/**
 * Checkout form rules from n8n config (`checkoutForm`).
 * If the webhook omits `checkoutForm`, {@link DEFAULT_CHECKOUT_FORM_BY_ID} applies by `id`.
 * Delivery amounts and thresholds come only from n8n (no static partner table in the app).
 */

/**
 * Canonical field lists per flow when le webhook n’envoie pas `checkoutForm`.
 * n8n peut n’envoyer que `id` + prix / commission : ce bloc est fusionné côté app.
 * @type {Record<string, {
 *   delivery: { required: string[], optional?: string[], readOnlyKeys?: string[] },
 *   billing: null | { required: string[], optional?: string[] },
 *   companyWhenChecked: null | { required: string[] },
 * }>}
 */
export const DEFAULT_CHECKOUT_FORM_BY_ID = {
  version1: {
    delivery: {
      required: [
        "deliveryDate",
        "latestDeliveryTime",
        "email",
        "googleMapsLink",
      ],
      optional: [
        "boatName",
        "boatModel",
        "recipientName",
        "recipientPhone",
        "deliveryNotes",
      ],
      readOnlyKeys: ["deliveryDate"],
    },
    billing: null,
    companyWhenChecked: null,
  },
  version2: {
    delivery: {
      required: ["deliveryDate", "email", "firstName", "lastName"],
      optional: ["boatName", "numberOfPeople", "deliveryNotes"],
      readOnlyKeys: ["deliveryDate"],
    },
    billing: {
      required: ["address1", "postcode", "city", "country"],
      optional: [],
    },
    companyWhenChecked: {
      required: ["companyName", "companyRegistration"],
    },
  },
  version3: {
    delivery: {
      required: ["deliveryDate", "email", "firstName", "lastName"],
      optional: ["boatName", "numberOfPeople", "deliveryNotes"],
      readOnlyKeys: ["deliveryDate"],
    },
    billing: {
      required: ["address1", "postcode", "city", "country"],
      optional: [],
    },
    companyWhenChecked: {
      required: ["companyName", "companyRegistration"],
    },
  },
  versionNoCheckout: {
    delivery: {
      required: ["deliveryDate", "email", "firstName", "lastName"],
      optional: ["boatName", "numberOfPeople", "deliveryNotes"],
      readOnlyKeys: ["deliveryDate"],
    },
    billing: {
      required: ["address1", "postcode", "city", "country"],
      optional: [],
    },
    companyWhenChecked: {
      required: ["companyName", "companyRegistration"],
    },
  },
};

/**
 * @param {object|null|undefined} config
 * @returns {object}
 */
export function normalizeCheckoutConfig(config) {
  if (!config) return {};
  const id = config.id;

  const deliveryCost =
    config.deliveryCost != null && !Number.isNaN(Number(config.deliveryCost))
      ? Number(config.deliveryCost)
      : 0;

  const deliveryFreeThreshold =
    config.deliveryFreeThreshold != null &&
    !Number.isNaN(Number(config.deliveryFreeThreshold))
      ? Number(config.deliveryFreeThreshold)
      : -1;

  const applyPartnerDiscount =
    config.applyPartnerDiscount !== undefined
      ? Boolean(config.applyPartnerDiscount)
      : id === "version1";

  const deliveryFeeDefaultOn =
    id === "version1" || id === "version2" || id === "version3";

  const applyDeliveryFee =
    config.applyDeliveryFee !== undefined
      ? Boolean(config.applyDeliveryFee)
      : deliveryFeeDefaultOn;

  let showPartnerDiscountInSummary = config.showPartnerDiscountInSummary;
  if (showPartnerDiscountInSummary === undefined) {
    showPartnerDiscountInSummary = applyPartnerDiscount && id === "version1";
  } else {
    showPartnerDiscountInSummary = Boolean(showPartnerDiscountInSummary);
  }

  let showDeliveryFeeInSummary = config.showDeliveryFeeInSummary;
  if (showDeliveryFeeInSummary === undefined) {
    showDeliveryFeeInSummary = applyDeliveryFee && deliveryCost > 0;
  } else {
    showDeliveryFeeInSummary = Boolean(showDeliveryFeeInSummary);
  }

  const checkoutForm =
    config.checkoutForm ?? DEFAULT_CHECKOUT_FORM_BY_ID[id] ?? null;

  return {
    ...config,
    deliveryCost,
    deliveryFreeThreshold,
    applyPartnerDiscount,
    applyDeliveryFee,
    showPartnerDiscountInSummary,
    showDeliveryFeeInSummary,
    checkoutForm,
  };
}

/**
 * @param {object} config normalized or raw
 */
export function isPartnerCheckout(config) {
  return config?.id === "version1";
}

/**
 * @param {object} config normalized (with checkoutForm)
 */
export function shouldShowBillingSection(config) {
  return config?.checkoutForm?.billing != null;
}

/**
 * Delivery date read from catalog on checkout (not edited in form).
 * @param {object} config normalized
 */
export function isDeliveryDateReadOnly(config) {
  const keys = config?.checkoutForm?.delivery?.readOnlyKeys;
  if (Array.isArray(keys)) return keys.includes("deliveryDate");
  const id = config?.id;
  return (
    id === "version1" ||
    id === "version2" ||
    id === "version3" ||
    id === "versionNoCheckout"
  );
}

/**
 * @param {object} config normalized
 * @param {string} fieldKey form state key
 * @param {{ companyCheck?: boolean }} ctx
 */
export function isCheckoutFieldRequired(config, fieldKey, ctx = {}) {
  const spec = config?.checkoutForm;
  if (!spec) return false;
  if (spec.delivery?.required?.includes(fieldKey)) return true;
  if (spec.billing?.required?.includes(fieldKey)) return true;
  if (
    ctx.companyCheck &&
    spec.companyWhenChecked?.required?.includes(fieldKey)
  ) {
    return true;
  }
  return false;
}

/**
 * Rental agreement checkbox for paid checkout flows.
 * @param {object} config normalized or raw
 */
export function shouldShowRentalAgreement(config) {
  return Boolean(config) && !config.noCheckout;
}

/**
 * Field names that must be non-empty (string trim) or Date for deliveryDate.
 * @param {object} config normalized
 * @param {{ companyCheck?: boolean }} ctx
 * @returns {string[]}
 */
export function getCheckoutRequiredFieldNames(config, ctx = {}) {
  const companyCheck = Boolean(ctx.companyCheck);
  const spec = config.checkoutForm;
  if (!spec) return [];

  const names = [...(spec.delivery?.required || [])];
  if (spec.billing) {
    names.push(...(spec.billing.required || []));
  }
  if (companyCheck && spec.companyWhenChecked?.required?.length) {
    names.push(...spec.companyWhenChecked.required);
  }
  return names;
}
